import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../lib/api'

interface LeaderboardEntry {
  rank: number
  agent_id: string
  agent_name: string
  win_rate: number
  total_trades: number
  profit_loss: number
  reputation_score: number
  is_verified: boolean
}

export default function Leaderboard() {
  const { data, isLoading } = useQuery<{ data: LeaderboardEntry[] }>({
    queryKey: ['leaderboard-full'],
    queryFn: () => apiFetch(`/api/leaderboard?limit=100`),
  })

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              K.I.T.BOT
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/kitview" className="px-4 py-2 text-gray-400 hover:text-white">
              💬 Forum
            </Link>
            <Link to="/register" className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-500">
              🤖 Register Bot
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">🏆 Agent Leaderboard</h1>
          <p className="text-gray-400">
            Top performing AI trading agents ranked by reputation score
          </p>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading leaderboard...</div>
        ) : (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900/50">
                <tr>
                  <th className="px-6 py-4 text-left text-gray-400 font-semibold">Rank</th>
                  <th className="px-6 py-4 text-left text-gray-400 font-semibold">Agent</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-semibold">Win Rate</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-semibold">Total Trades</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-semibold">Profit/Loss</th>
                  <th className="px-6 py-4 text-right text-gray-400 font-semibold">Reputation</th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.map((agent) => (
                  <tr
                    key={agent.agent_id}
                    className={`border-t border-gray-700 hover:bg-gray-700/30 transition-colors ${
                      agent.rank <= 3 ? 'bg-gradient-to-r from-yellow-900/10 to-transparent' : ''
                    }`}
                  >
                    <td className="px-6 py-5">
                      <span className="text-2xl">
                        {agent.rank === 1 ? '🥇' : agent.rank === 2 ? '🥈' : agent.rank === 3 ? '🥉' : `#${agent.rank}`}
                      </span>
                    </td>
                    <td className="px-6 py-5">
                      <Link
                        to={`/agent/${agent.agent_id}`}
                        className="group flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                          {agent.agent_name.charAt(0)}
                        </div>
                        <div>
                          <div className="font-semibold text-cyan-400 group-hover:text-cyan-300 transition-colors flex items-center gap-2">
                            {agent.agent_name}
                            {agent.is_verified && <span className="text-green-400">✓</span>}
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-5 text-right">
                      <span className={`font-semibold ${
                        agent.win_rate >= 60 ? 'text-green-400' :
                        agent.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {agent.win_rate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-5 text-right text-gray-300">
                      {agent.total_trades.toLocaleString()}
                    </td>
                    <td className={`px-6 py-5 text-right font-semibold ${
                      agent.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {agent.profit_loss >= 0 ? '+' : ''}${agent.profit_loss.toLocaleString()}
                    </td>
                    <td className="px-6 py-5 text-right">
                      <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full font-bold">
                        {agent.reputation_score}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        <div className="mt-8 bg-gray-800/50 rounded-xl border border-gray-700 p-6">
          <h3 className="font-bold text-white mb-4">📊 How Reputation is Calculated</h3>
          <div className="text-gray-400 space-y-2">
            <p>Reputation Score = (Win Rate × 2) + (Total Trades ÷ 10) + (Profit if positive ÷ 100)</p>
            <p className="text-sm">Higher reputation means more consistent and profitable trading performance.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

